import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

const mathRemarkPlugins = [remarkMath, remarkInlineMathDisplayStyle];
const mathRehypePlugins = [[rehypeKatex, {
  errorColor: '#c53d4a',
  output: 'htmlAndMathml',
  strict: false,
  throwOnError: false,
  trust: false,
}]];
const displayOperatorPattern = /\\(?:sum|prod|coprod|lim(?:sup|inf)?|max|min|sup|inf|int|iint|iiint|oint|big(?:cap|cup|sqcup|vee|wedge|odot|otimes|oplus|uplus))(?![A-Za-z])/;

export function normalizeMathMarkdown(value) {
  return value
    .replace(/\\\[([\s\S]*?)\\\]/g, (_match, expression) => `\n\n$$\n${expression.trim()}\n$$\n\n`)
    .replace(/\\\(([\s\S]*?)\\\)/g, (_match, expression) => `$${expression.trim()}$`);
}

const delimitedMathPattern = /(\$\$[\s\S]*?\$\$|\$(?:\\.|[^$\r\n])+\$)/g;
const bareLatexChunkPattern = /[^\u1100-\u11ff\u3130-\u318f\uac00-\ud7af\r\n]*\\[A-Za-z]+[^\u1100-\u11ff\u3130-\u318f\uac00-\ud7af\r\n]*/g;

export function normalizeBareLatexMarkdown(value) {
  const normalized = normalizeMathMarkdown(value);
  return normalized
    .split(delimitedMathPattern)
    .map((part) => {
      if (!part || (part.startsWith('$') && part.endsWith('$'))) return part;
      return part.replace(bareLatexChunkPattern, (chunk) => {
        const expression = chunk.trim();
        if (!expression) return chunk;
        const leadingSpace = chunk.match(/^\s*/)?.[0] || '';
        const trailingSpace = chunk.match(/\s*$/)?.[0] || '';
        return `${leadingSpace}$${expression}$${trailingSpace}`;
      });
    })
    .join('');
}

export function applyDisplayStyleToInlineMath(node) {
  if (!node || typeof node !== 'object') return node;
  if (node.type === 'inlineMath' && typeof node.value === 'string') {
    const expression = node.value.trim();
    if (
      expression
      && !expression.startsWith('\\displaystyle')
      && displayOperatorPattern.test(expression)
    ) {
      const displayExpression = `\\displaystyle ${expression}`;
      node.value = displayExpression;
      if (Array.isArray(node.data?.hChildren)) {
        node.data.hChildren.forEach((child) => {
          if (child?.type === 'text') child.value = displayExpression;
        });
      }
    }
  }
  if (Array.isArray(node.children)) {
    node.children.forEach(applyDisplayStyleToInlineMath);
  }
  return node;
}

function remarkInlineMathDisplayStyle() {
  return applyDisplayStyleToInlineMath;
}

function MathMarkdown({ value, className = '', renderBareLatex = false }) {
  if (typeof value !== 'string' || !value.trim()) return null;
  const classes = ['ai-essay-rich-text', className].filter(Boolean).join(' ');
  const normalizedValue = renderBareLatex
    ? normalizeBareLatexMarkdown(value)
    : normalizeMathMarkdown(value);

  return (
    <div className={classes}>
      <ReactMarkdown remarkPlugins={mathRemarkPlugins} rehypePlugins={mathRehypePlugins}>
        {normalizedValue}
      </ReactMarkdown>
    </div>
  );
}

export default MathMarkdown;
